import {createContext, useContext, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import Config from "../config";
import {useLocalStorage} from "./useLocalStorage";

/*NOTES:
mfaRegister = True when dialog to register MFA shall be opened
mfaVerify = True after registering to verify the first QR code
mfa

*/

type ErrorProps = {
  errorLogin: string;
  errorMfaRegister: string;
  errorMfaChallenge: string;
  errorPwdChange: string;
}

const initialError: ErrorProps = {
  errorLogin: "",
  errorMfaRegister: "",
  errorMfaChallenge: "",
  errorPwdChange: ""
};

const AuthContext = createContext<{
  accessToken: string;
  refreshToken: string;
  error: ErrorProps;
  mfaRegister: boolean;
  mfaChallenge: boolean;
  mfaQRCode: string;
  pwdChangeRequired: boolean;
  isLoading: boolean;
  onLogin: (username: string, password: string) => void;
  onMfaChallenge: (otp: string, username: string, password: string) => void;
  onMfaRegister: (otp: string, username: string, password: string) => void;
  onPwdChange: (newPassword: string, accessToken: string) => void;
  onLogout: () => void;
  onRefresh: () => void;
  onInvalidToken: () => void;
}>({
  accessToken: "",
  refreshToken: "",
  error: {errorLogin: "",
          errorMfaRegister: "",
          errorMfaChallenge: "",
          errorPwdChange: ""},
  mfaRegister: false,
  mfaChallenge: false,
  mfaQRCode: "",
  pwdChangeRequired: false,
  isLoading: false,
  onLogin: () => {},
  onMfaChallenge: () => {},
  onMfaRegister: () => {},
  onPwdChange: () => {},
  onLogout: () => {},
  onRefresh: () => {},
  onInvalidToken: () => {}
});

type AuthProviderProps = {
  children: React.ReactNode;
};

type TokenProps = {
  accessToken: string;
  refreshToken: string;
}

type MFAProps = {
  mfaRequired: boolean | null;
  mfaEnabled: boolean | null;
}

type PWDProps = {
  pwdChangeRequired: boolean | null;
}

type QRCodeProps = {
  qrCode: string;
}

type LoginProps = {
  tokens: TokenProps,
  mfa: MFAProps,
  pwd: PWDProps,
}

const fakeAuth = async(username: string, password: string) => {
  const tokens: TokenProps = {accessToken: "",
                              refreshToken: ""};
  const mfa: MFAProps = {mfaRequired: null,
                         mfaEnabled: null}

  const pwd: PWDProps = {pwdChangeRequired: null}

  const loginInformation: LoginProps = {tokens: tokens,
                                        mfa: mfa,
                                        pwd: pwd}

  return loginInformation;
};

const startAuthLogin = async(username: string, password: string) => {
  //login via username and password

  const headers: Headers = new Headers();

  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json");

  let url = `${Config.getInstance().getCoordinationServiceURL()}/api/auth/login/crd`;

  const tokens: TokenProps = {accessToken: "",
                              refreshToken: ""}

  const mfa: MFAProps = {mfaRequired: null,
                         mfaEnabled: null}

  const pwd: PWDProps = {pwdChangeRequired: null}

  const loginInformation: LoginProps = {tokens: tokens,
                                        mfa: mfa,
                                        pwd: pwd}

  const response = await fetch(url, {
                     method: "POST",
                     headers: headers,
                     body: JSON.stringify({
                       username: username,
                       password: password,
                     }
                 )
               })

  if (!response.ok) {
    return loginInformation;
  }

  const data = await response.json();

  if(!data.hasOwnProperty('mfa_required')){
    return loginInformation
  }
  if(!data.hasOwnProperty('mfa_enabled')){
    return loginInformation
  }
  if(!data.hasOwnProperty('pwdchange_required')){
    return loginInformation
  }
  if(!data.hasOwnProperty('access')){
    return loginInformation
  }
  if(!data.hasOwnProperty('refresh')){
    return loginInformation
  }
  if(data.access){
    tokens.accessToken = data.access
  }
  if(data.refresh){
    tokens.refreshToken = data.refresh
  }

  mfa.mfaRequired = data.mfa_required;
  mfa.mfaEnabled = data.mfa_enabled;
  pwd.pwdChangeRequired = data.pwdchange_required;

  return loginInformation;

}



const startAuthMfa = async(otp: string, username: string, password: string) => {
  //login via one-time-password, username and password

  const headers: Headers = new Headers();

  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json");

  let url = `${Config.getInstance().getCoordinationServiceURL()}/api/auth/token/get`;

  const response = await fetch(url, {
                     method: "POST",
                     headers: headers,
                     body: JSON.stringify({
                       username: username,
                       password: password,
                       otp: otp
                     }
                 )
  })

  const tokens: TokenProps = {accessToken: "",
                              refreshToken: ""}

  const mfa: MFAProps = {mfaRequired: null,
                         mfaEnabled: null}

  const pwd: PWDProps = {pwdChangeRequired: null}

  const loginInformation: LoginProps = {tokens: tokens,
                                        mfa: mfa,
                                        pwd: pwd}

  if(!response.ok) {
    return loginInformation;
  }

  const data = await response.json();

  if(!data.access){
    return loginInformation
  }
  if(!data.refresh){
    return loginInformation
  }
  if(!data.hasOwnProperty('pwdchange_required')){
    return loginInformation
  }

  pwd.pwdChangeRequired = data.pwdchange_required;

  tokens.accessToken = data.access
  tokens.refreshToken = data.refresh

  return loginInformation;

}

const retrieveMfaQRCode = async(username: string, password: string) => {
  //login via username and password

  const headers: Headers = new Headers();

  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json");

  let url = `${Config.getInstance().getCoordinationServiceURL()}/api/auth/register/mfa`;

  const response = await fetch(url, {
                     method: "POST",
                     headers: headers,
                     body: JSON.stringify({
                       username: username,
                       password: password,
                     }
                 )
  })

  const qrCode: QRCodeProps = {qrCode: ""}

  if (!response.ok) {
    return qrCode;
  }

  const data = await response.json();

  if(!data.qr_code){
    return qrCode
  }

  qrCode.qrCode = data.qr_code

  return qrCode;

}

const verifyMfaOtp = async(otp: string, username: string, password: string) => {
  //login via username and password

  const headers: Headers = new Headers();

  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json");

  let url = `${Config.getInstance().getCoordinationServiceURL()}/api/auth/verify/mfa`;

  const response = await fetch(url, {
                     method: "POST",
                     headers: headers,
                     body: JSON.stringify({
                       username: username,
                       password: password,
                       otp: otp
                     }
                 )
  })

  const tokens: TokenProps = {accessToken: "",
                              refreshToken: ""}


  if(!response.ok) {
    return tokens;
  }

  const data = await response.json();

  if(!data.access){
    return tokens
  }
  if(!data.refresh){
    return tokens
  }

  tokens.accessToken = data.access
  tokens.refreshToken = data.refresh

  return tokens;

}

const refreshAuth = async(refreshToken: string) => {

  const headers: Headers = new Headers();

  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json");

  let url = `${Config.getInstance().getCoordinationServiceURL()}/api/token/refresh`;

  const response = await fetch(url, {
                     method: "POST",
                     headers: headers,
                     body: JSON.stringify({
                       refreshToken: refreshToken,
                     }
                 )
  })

  const tokens: TokenProps = {accessToken: "",
                              refreshToken: ""}

  if (!response.ok) {
    return tokens;
  }

  const data = await response.json();

  if (!data.access){
    return tokens;
  }

  if (!data.refresh){
    return tokens;
  }

  tokens.accessToken = data.access;
  tokens.refreshToken = data.refresh;

  return tokens;
}



export const AuthProvider = ({children}: AuthProviderProps) => {

  //useLocalStorage to receive a token
  const [accessToken, setAccessToken] = useLocalStorage('accessToken', "")
  const [refreshToken, setRefreshToken] = useLocalStorage('refreshToken', "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorProps>({...initialError});
  const [mfaChallenge, setMfaChallenge] = useState(false);
  const [mfaRegister, setMfaRegister] = useState(false);
  const [mfaQRCode, setMfaQRCode] = useState("");
  const [pwdChangeRequired, setPwdChangeRequired] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async(username: string, password: string) => {

    setIsLoading(true);
    setError({...initialError});

    try {
      const loginInformation = await startAuthLogin(username, password);
      const tokens = loginInformation.tokens;
      const mfaInformation = loginInformation.mfa;
      const pwdInformation = loginInformation.pwd;

      if(mfaInformation.mfaEnabled === true){
        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        setMfaChallenge(true);
        setIsLoading(false);
        return
      } else if(mfaInformation.mfaRequired === true){
        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        const qrCode = await retrieveMfaQRCode(username, password);
        setMfaQRCode(qrCode.qrCode)
        setMfaRegister(true);
        setIsLoading(false);
        return
      }

      if(!tokens.accessToken){
        const errorTemp = {...error};
        errorTemp.errorLogin = 'Access denied'
        setError(errorTemp);

        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        setIsLoading(false);
        return
      } else{
        window.localStorage.setItem('accessToken', tokens.accessToken);
        window.localStorage.setItem('refreshToken', tokens.refreshToken);
      }

      setIsLoading(false);

      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);

      if(pwdInformation.pwdChangeRequired === true){
        setPwdChangeRequired(true);
        setIsLoading(false);
        return
      }

      //set redirect page when login
      const origin = location.state?.from?.pathname || '/';
      navigate(origin);
    } catch(e){
      const errorTemp = {...error};
      errorTemp.errorLogin = 'Unhandled Error occurred'
      setError(errorTemp);
      window.localStorage.removeItem('accessToken');
      window.localStorage.removeItem('refreshToken');
      setIsLoading(false)
      return
    }
  };

  const handleMfaChallenge = async(otp: string, username: string, password: string) => {

    setIsLoading(true);
    setError({...initialError});

    const loginInformation = await startAuthMfa(otp, username, password);
    const tokens = loginInformation.tokens;
    const mfaInformation = loginInformation.mfa;
    const pwdInformation = loginInformation.pwd;

    if(!tokens.accessToken){
      const errorTemp = {...error};
      errorTemp.errorMfaChallenge = 'MFA token expired'
      setError(errorTemp);

      window.localStorage.removeItem('accessToken');
      window.localStorage.removeItem('refreshToken');
      setIsLoading(false);
      return
    } else{
      window.localStorage.setItem('accessToken', tokens.accessToken);
      window.localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    setIsLoading(false);

    setAccessToken(tokens.accessToken);
    setRefreshToken(tokens.refreshToken);

    setMfaChallenge(false);

    if(pwdInformation.pwdChangeRequired === true){
      setPwdChangeRequired(true);
      setIsLoading(false);
      return
    }

    //set redirect page when login
    const origin = location.state?.from?.pathname || '/';
    navigate(origin);

  };

  const handleMfaRegister = async(otp: string, username: string, password: string) => {

    setIsLoading(true);
    setError({...initialError});

    const tokens = await verifyMfaOtp(otp, username, password);

    if(!tokens.accessToken){
      const errorTemp = {...error};
      errorTemp.errorMfaRegister = 'MFA token invalid'
      setError(errorTemp);

      window.localStorage.removeItem('accessToken');
      window.localStorage.removeItem('refreshToken');
      setIsLoading(false);
      return
    } else{
      window.localStorage.setItem('accessToken', tokens.accessToken);
      window.localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    setIsLoading(false);

    setAccessToken(tokens.accessToken);
    setRefreshToken(tokens.refreshToken);

    setMfaRegister(false);

    //set redirect page when login
    const origin = location.state?.from?.pathname || '/';
    navigate(origin);

  };

  const handlePwdChange = async(newPassword: string, accessToken: string) => {

    setIsLoading(true);

    setError({...initialError});

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/auth/reset/pwd`;

    const headers = new Headers();

    const requestBody = {"new_password": newPassword};

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    try {
      const response = await fetch(url, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(requestBody)
        })

      if (!response.ok) {
        const errorTemp = {...error};
        errorTemp.errorPwdChange = 'Unhandled Error occurred'
        setError(errorTemp);
        setIsLoading(false)
        return
      }

      //password has been changed
      setIsLoading(false);
      setPwdChangeRequired(false);

      const origin = location.state?.from?.pathname || '/';
      navigate(origin);


    } catch (e){
        const errorTemp = {...error};
        errorTemp.errorPwdChange = 'Unhandled Error occurred'
        setError(errorTemp);
        setIsLoading(false)
        return
    }

  };


  const handleLogout = () => {
    setAccessToken("");
    setRefreshToken("");
    setMfaChallenge(false);
    setMfaRegister(false);
    setPwdChangeRequired(false);
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const handleInvalidToken = () => {
    setAccessToken("");
    setRefreshToken("");
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('refreshToken');
    navigate('/login');
  }

  const handleRefresh = async() => {
    //try to get token and refresh token from local storage
    //if access token found in local storage, then try to authenticate with this accessToken
    //or rather use a refresh token

    const localRefreshToken = window.localStorage.getItem('refreshToken');

    if(localRefreshToken){
      const thisLocalRefreshToken = JSON.parse(localRefreshToken);
      setIsLoading(true);
      const tokens = await refreshAuth(thisLocalRefreshToken);
      setIsLoading(false);

      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      console.log(tokens);
      if(!tokens.accessToken){
        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        navigate('/login');
      } else{
        window.localStorage.setItem('accessToken', tokens.accessToken);
        window.localStorage.setItem('refreshToken', tokens.refreshToken);
      }
    }
  }

  const value = {
    error,
    mfaChallenge,
    mfaRegister,
    mfaQRCode,
    pwdChangeRequired,
    isLoading,
    accessToken,
    refreshToken,
    onLogin: handleLogin,
    onMfaChallenge: handleMfaChallenge,
    onMfaRegister: handleMfaRegister,
    onPwdChange: handlePwdChange,
    onLogout: handleLogout,
    onRefresh: handleRefresh,
    onInvalidToken: handleInvalidToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;

}

export const useAuth = () => {
  return useContext(AuthContext);
}
